/**
 * PCE Pagination Enhancement
 * SEO-safe infinite scroll with progressive enhancement
 *
 * Features:
 * - Intercepts pagination clicks, loads via AJAX
 * - "Load More" button for explicit user action
 * - Auto-loads next page if current page too sparse
 * - Updates URL with pushState (bookmarkable)
 * - Browser back/forward support
 * - Works without JavaScript (progressive enhancement)
 * - Maintains proper SEO with rel next/prev links
 */

class PCEPagination {
    constructor(container) {
        this.container = container;
        this.productsContainer = container.querySelector('[data-products-container]');
        this.paginationWrapper = container.querySelector('[data-pagination-wrapper]');
        this.loadMoreWrapper = container.querySelector('[data-load-more]');
        this.loadMoreButton = container.querySelector('[data-action="load-more"]');

        if (!this.productsContainer) return;

        // Configuration from server-rendered data
        this.targetTag = this.productsContainer.dataset.targetTag;
        this.currentPage = parseInt(this.productsContainer.dataset.currentPage) || 1;
        this.totalPages = parseInt(this.productsContainer.dataset.totalPages) || 1;
        this.baseUrl = this.productsContainer.dataset.baseUrl || window.location.pathname;
        this.isPCE = this.paginationWrapper?.dataset.isPce === 'true';
        this.minProductsPerPage = 6; // Auto-load next if fewer than this
        this.loading = false;
        this.parser = new DOMParser();

        // Track which pages have been loaded
        this.loadedPages = new Set([this.currentPage]);

        // Track scroll position for URL updates
        this.pageRanges = new Map(); // Maps page number to element range

        this.init();
    }

    init() {
        // For PCE pages, enhance with infinite scroll but KEEP URLs for SEO
        if (this.isPCE) {
            // Hide traditional pagination UI but keep URLs functional
            this.hideRegularPagination();

            // Store initial page products for scroll tracking
            this.updatePageRanges();

            // If we're on page 2+, acknowledge that in the UI
            if (this.currentPage > 1) {
                this.showCurrentPageStatus();
            }
        }

        // Progressive enhancement: Show load more button
        if (this.loadMoreWrapper && this.currentPage < this.totalPages) {
            this.showLoadMoreButton();
        }

        // Bind events
        if (this.loadMoreButton) {
            this.loadMoreButton.addEventListener('click', () => this.loadNextPage());
        }

        // Intercept pagination link clicks (fallback if load more not shown)
        if (!this.isPCE) {
            this.interceptPaginationLinks();
        }

        // Handle sparse pages: auto-load next if too few products
        this.handleSparsePage();

        // Handle browser back/forward
        window.addEventListener('popstate', (e) => this.handlePopState(e));

        // Auto-load on sparse page button
        const loadNextBtn = document.querySelector('[data-action="load-next-page"]');
        if (loadNextBtn) {
            loadNextBtn.addEventListener('click', () => this.loadNextPage());
        }

        // Set up scroll observer for URL updates (PCE only)
        if (this.isPCE) {
            this.setupScrollObserver();
        }
    }

    /**
     * Show status for users who land on page 2+
     */
    showCurrentPageStatus() {
        if (this.currentPage > 1) {
            const status = document.createElement('div');
            status.className = 'pce-page-status';
            status.innerHTML = `
                <p style="text-align: center; color: var(--text-color-light); margin: 1rem 0;">
                    Showing page ${this.currentPage} of ${this.totalPages}
                </p>
            `;
            this.productsContainer.parentElement.insertBefore(status, this.productsContainer);
        }
    }

    /**
     * Track which products belong to which page
     */
    updatePageRanges() {
        const products = this.productsContainer.querySelectorAll('.product-item:not([data-preloaded="true"])');
        if (products.length === 0) return;

        // Find products for current pages
        this.loadedPages.forEach(pageNum => {
            const pageProducts = Array.from(products).filter(p => {
                const productPage = parseInt(p.dataset.pageNum) || this.currentPage;
                return productPage === pageNum;
            });

            if (pageProducts.length > 0) {
                this.pageRanges.set(pageNum, {
                    first: pageProducts[0],
                    last: pageProducts[pageProducts.length - 1],
                    count: pageProducts.length
                });
            }
        });
    }

    /**
     * Set up scroll observer to update URL as user scrolls
     */
    setupScrollObserver() {
        // Debounced scroll handler
        let scrollTimeout;
        const handleScroll = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => this.updateURLBasedOnScroll(), 150);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
    }

    /**
     * Update URL based on which page is most visible
     */
    updateURLBasedOnScroll() {
        if (!this.isPCE || this.pageRanges.size === 0) return;

        const viewportMiddle = window.scrollY + window.innerHeight / 2;
        let mostVisiblePage = 1;

        // Find which page range is most visible
        this.pageRanges.forEach((range, pageNum) => {
            const rect = range.first.getBoundingClientRect();
            const elementTop = rect.top + window.scrollY;
            const elementBottom = range.last.getBoundingClientRect().bottom + window.scrollY;

            if (viewportMiddle >= elementTop && viewportMiddle <= elementBottom) {
                mostVisiblePage = pageNum;
            }
        });

        // Update URL without triggering navigation
        const currentUrlPage = new URLSearchParams(window.location.search).get('page') || '1';
        if (parseInt(currentUrlPage) !== mostVisiblePage) {
            const newUrl = mostVisiblePage === 1
                ? this.baseUrl
                : `${this.baseUrl}?page=${mostVisiblePage}`;

            history.replaceState(
                { page: mostVisiblePage, pce: true },
                '',
                newUrl
            );
        }
    }

    /**
     * Show load more button and hide regular pagination for better UX
     */
    showLoadMoreButton() {
        if (this.loadMoreWrapper) {
            this.loadMoreWrapper.style.display = 'block';
        }
    }

    /**
     * Hide regular pagination links for PCE infinite scroll
     */
    hideRegularPagination() {
        // Immediately hide all pagination elements
        const allPagination = this.paginationWrapper?.querySelectorAll('.pce-pagination-fallback, .pagination');
        allPagination?.forEach(elem => {
            elem.style.display = 'none';
        });

        // Inject aggressive CSS to ensure pagination NEVER shows for PCE pages
        if (!document.getElementById('pce-pagination-override')) {
            const style = document.createElement('style');
            style.id = 'pce-pagination-override';
            style.textContent = `
                /* Force hide ALL pagination for PCE pages */
                [data-pce-pagination] .pce-pagination-fallback,
                [data-pce-pagination] .pagination,
                [data-is-pce="true"] .pce-pagination-fallback,
                [data-is-pce="true"] .pagination {
                    display: none !important;
                    visibility: hidden !important;
                    height: 0 !important;
                    overflow: hidden !important;
                }

                /* Ensure load more is visible */
                [data-pce-pagination] .load-more-wrapper {
                    display: block !important;
                }
            `;
            document.head.appendChild(style);
        }
    }

    /**
     * Intercept pagination link clicks for smooth loading
     */
    interceptPaginationLinks() {
        const paginationLinks = this.container.querySelectorAll('.pagination__nav-item a');

        paginationLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const url = link.href;
                this.loadPage(url, false); // Don't append, replace
            });
        });
    }

    /**
     * Check if current page is sparse and auto-load next
     */
    handleSparsePage() {
        if (!this.paginationWrapper) return;

        const productsFound = parseInt(this.paginationWrapper.dataset.productsFound) || 0;
        const hasNextPage = this.currentPage < this.totalPages;

        // If page has fewer products than threshold and there's a next page, auto-load
        if (productsFound < this.minProductsPerPage && hasNextPage && productsFound > 0) {
            console.log(`Sparse page detected (${productsFound} products), auto-loading next page...`);

            // Small delay to allow page to render
            setTimeout(() => {
                this.loadNextPage(true); // true = auto-load (silent)
            }, 500);
        }
    }

    /**
     * Load next page
     */
    async loadNextPage(autoLoad = false) {
        if (this.loading) return;
        if (this.currentPage >= this.totalPages) {
            this.showStatus('No more pages to load');
            return;
        }

        // Try to load from cache first if preloader is available
        if (window.pcePreloader && window.pcePreloader.isPreloadComplete()) {
            const loaded = this.loadFromCache();
            if (loaded) {
                return; // Successfully loaded from cache
            }
        }

        // Fallback to AJAX loading
        const nextPage = this.currentPage + 1;
        const nextUrl = this.getPageUrl(nextPage);

        await this.loadPage(nextUrl, true, autoLoad);
    }

    /**
     * Load products from preloader cache
     */
    loadFromCache() {
        const preloader = window.pcePreloader;
        if (!preloader) return false;

        const nextPage = this.currentPage + 1;
        const cachedProducts = preloader.getCachedProducts(nextPage);

        if (cachedProducts.length === 0) {
            return false;
        }

        console.log(`Loading page ${nextPage} from cache (${cachedProducts.length} products)`);

        // Mark products with their page number for tracking
        cachedProducts.forEach(product => {
            product.style.display = ''; // Make visible
            product.dataset.preloaded = 'false'; // Mark as loaded
            product.dataset.pageNum = nextPage; // Track which page this came from
            this.productsContainer.appendChild(product);
        });

        // Update tracking
        this.currentPage = nextPage;
        this.loadedPages.add(nextPage);
        this.productsContainer.dataset.currentPage = this.currentPage;

        // Update page ranges for scroll tracking
        this.updatePageRanges();

        // Update or hide load more button
        if (this.currentPage >= this.totalPages) {
            this.hideLoadMoreButton();
            this.showStatus('All products loaded');
        } else {
            this.showStatus(`Loaded page ${nextPage} products`);
        }

        // Re-run out-of-stock sorter if available
        if (window.CollectionPerformance?.OutOfStockSorter) {
            new window.CollectionPerformance.OutOfStockSorter();
        }

        // Trigger product list update for filters
        const pceTemplate = document.querySelector('pce-template');
        if (pceTemplate && pceTemplate.updateProductList) {
            pceTemplate.updateProductList();
        }

        return true;
    }

    /**
     * Load a specific page via AJAX
     */
    async loadPage(url, append = true, autoLoad = false) {
        if (this.loading) return;

        this.loading = true;
        this.showLoadingState(autoLoad);

        try {
            // For PCE pages, ensure we're loading with page param but not updating the URL
            const loadUrl = this.isPCE && append ?
                `${this.baseUrl}?page=${this.currentPage + 1}` : url;

            const response = await fetch(loadUrl);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const html = await response.text();
            const doc = this.parser.parseFromString(html, 'text/html');

            // Extract products from loaded page
            const loadedProductsContainer = doc.querySelector('[data-products-container]');

            if (!loadedProductsContainer) {
                throw new Error('Products container not found in loaded page');
            }

            // Get product elements (excluding empty state)
            const productItems = Array.from(loadedProductsContainer.children).filter(
                child => !child.hasAttribute('data-sparse-page')
            );

            if (productItems.length === 0) {
                console.log('No products found on next page, trying next...');

                // If auto-loading and page is empty, try next page
                if (append && this.currentPage + 1 < this.totalPages) {
                    this.currentPage++;
                    this.hideLoadingState();
                    this.loading = false;
                    await this.loadNextPage(true);
                    return;
                }

                this.showStatus('No more products to load');
                this.hideLoadMoreButton();
                this.hideLoadingState();
                this.loading = false;
                return;
            }

            if (append) {
                // Append products to existing list with page tracking
                const loadedPage = this.currentPage + 1;
                productItems.forEach(item => {
                    item.dataset.pageNum = loadedPage; // Track which page this came from
                    this.productsContainer.appendChild(item);
                });

                // Update tracking
                this.currentPage = loadedPage;
                this.loadedPages.add(loadedPage);
                this.productsContainer.dataset.currentPage = this.currentPage;

                // Update page ranges for scroll tracking
                this.updatePageRanges();

                // Update load more button
                if (this.loadMoreButton && this.currentPage < this.totalPages) {
                    const nextUrl = `${this.baseUrl}?page=${this.currentPage + 1}`;
                    this.loadMoreButton.dataset.nextUrl = nextUrl;
                } else {
                    this.hideLoadMoreButton();
                }

                // Re-run out-of-stock sorter if available
                if (window.CollectionPerformance?.OutOfStockSorter) {
                    new window.CollectionPerformance.OutOfStockSorter();
                }

                // Show success message
                if (!autoLoad) {
                    this.showStatus(`Loaded page ${loadedPage} (${productItems.length} products)`);
                }

                // Check if new page is also sparse
                setTimeout(() => {
                    const newProductsCount = productItems.length;
                    if (newProductsCount < this.minProductsPerPage && this.currentPage < this.totalPages) {
                        this.loadNextPage(true);
                    }
                }, 300);

            } else {
                // Replace products (used for pagination link clicks - non-PCE only)
                this.productsContainer.innerHTML = '';
                productItems.forEach(item => {
                    this.productsContainer.appendChild(item);
                });

                // Update page number from URL
                const urlParams = new URLSearchParams(new URL(url).search);
                this.currentPage = parseInt(urlParams.get('page')) || 1;
                this.productsContainer.dataset.currentPage = this.currentPage;

                // Update URL
                window.history.pushState(
                    { page: this.currentPage, pce: false },
                    '',
                    url
                );

                // Scroll to top of products
                this.productsContainer.scrollIntoView({ behavior: 'smooth' });
            }

        } catch (error) {
            console.error('Failed to load page:', error);
            this.showStatus('Failed to load products. Please try again.');
        } finally {
            this.hideLoadingState();
            this.loading = false;
        }
    }

    /**
     * Handle browser back/forward navigation
     */
    handlePopState(event) {
        // For PCE pages with proper infinite scroll, handle state changes
        if (this.isPCE && event.state?.pce) {
            const targetPage = event.state.page || 1;

            // If we've already loaded this page, scroll to it
            if (this.loadedPages.has(targetPage) && this.pageRanges.has(targetPage)) {
                const range = this.pageRanges.get(targetPage);
                if (range && range.first) {
                    range.first.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            } else if (targetPage !== this.currentPage) {
                // Need to reload the page to get correct server-rendered content
                window.location.reload();
            }
            return;
        }

        // For regular collections, reload if it was a pagination state
        if (!this.isPCE && event.state?.page) {
            window.location.reload();
        }
    }

    /**
     * Get page URL - now properly handles PCE infinite scroll
     */
    getPageUrl(pageNum) {
        const baseUrl = this.baseUrl || window.location.pathname;

        // Always return proper page URLs for SEO
        return pageNum === 1 ? baseUrl : `${baseUrl}?page=${pageNum}`;
    }

    /**
     * Show loading state
     */
    showLoadingState(silent = false) {
        if (this.loadMoreButton) {
            this.loadMoreButton.disabled = true;
            this.loadMoreButton.textContent = 'Loading...';
        }

        if (!silent) {
            this.showStatus('Loading products...');
        }
    }

    /**
     * Hide loading state
     */
    hideLoadingState() {
        if (this.loadMoreButton) {
            this.loadMoreButton.disabled = false;
            this.loadMoreButton.textContent = 'Load More Products';
        }
    }

    /**
     * Hide load more button when no more pages
     */
    hideLoadMoreButton() {
        if (this.loadMoreWrapper) {
            this.loadMoreWrapper.style.display = 'none';
        }
    }

    /**
     * Show status message
     */
    showStatus(message) {
        const statusEl = this.container.querySelector('.load-more-status');
        if (statusEl) {
            statusEl.textContent = message;

            // Clear after 3 seconds
            setTimeout(() => {
                statusEl.textContent = '';
            }, 3000);
        }
    }

    /**
     * Callback when preload is complete
     */
    onPreloadComplete() {
        console.log('PCE Pagination: Preload complete, Load More will now be instant');

        // Update button text to indicate instant loading
        if (this.loadMoreButton) {
            this.loadMoreButton.title = 'Products are preloaded for instant display';
        }
    }
}

/**
 * Initialize pagination enhancement
 */
function initPCEPagination() {
    const paginationContainers = document.querySelectorAll('[data-pce-pagination]');

    paginationContainers.forEach(container => {
        const instance = new PCEPagination(container);
        // Store instance globally for preloader integration
        window.pcePaginationInstance = instance;
    });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPCEPagination);
} else {
    initPCEPagination();
}

// Export for use in other scripts
window.PCEPagination = PCEPagination;
